import * as React from "react";
import { ChevronDown, Pencil } from "lucide-react";
import type { Expense, IncomeSummary, MonthlyFinancialSummary } from "@/types/tax";
import { expenseCategoryLabels } from "@/data/atoRules";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { claimableGstForExpense, gstFromGross } from "@/lib/gst";
import { currency } from "@/lib/utils";

export function MonthlyGstTable({
  rows,
  onEditIncome,
  onEditExpense,
}: {
  rows: MonthlyFinancialSummary[];
  onEditIncome: (income: IncomeSummary) => void;
  onEditExpense: (expense: Expense) => void;
}) {
  const [expanded, setExpanded] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (expanded && rows.some((row) => row.id === expanded)) return;
    if (expanded) setExpanded(null);
  }, [expanded, rows]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Monthly GST table</CardTitle>
        <CardDescription>Monthly income and expenses are kept separately so these records can support future income-tax reporting.</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-surface p-6 text-center">
            <p className="font-semibold text-ink">No monthly records yet</p>
            <p className="mt-1 text-sm text-muted">Add income and expenses to populate the table.</p>
          </div>
        ) : (
          <>
          <div className="grid gap-3 sm:hidden">
            {rows.map((row) => {
              const isExpanded = expanded === row.id;
              return (
                <div key={row.id} className="min-w-0 overflow-hidden rounded-lg border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-ink">{row.label}</p>
                      <p className={`tabular mt-1 text-lg font-semibold ${row.netGst >= 0 ? "text-payable" : "text-credit"}`}>
                        {currency(Math.abs(row.netGst))}
                      </p>
                      <Badge variant={row.netGst >= 0 ? "payable" : "credit"} className="mt-2">
                        {row.netGst >= 0 ? "GST payable" : "GST refundable"}
                      </Badge>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-expanded={isExpanded}
                      onClick={() => setExpanded(isExpanded ? null : row.id)}
                    >
                      Details
                      <ChevronDown className={`size-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} aria-hidden="true" />
                    </Button>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <MobileMetric label="Income" value={currency(row.incomeTotal)} />
                    <MobileMetric label="Expenses" value={currency(row.expenseTotal)} />
                    <MobileMetric label="GST on income" value={currency(row.gstOnSales)} tone="payable" />
                    <MobileMetric label="GST credits" value={currency(row.gstCredits)} tone="credit" />
                  </div>
                  {isExpanded ? (
                    <div className="mt-4 min-w-0 overflow-hidden border-t border-border pt-4">
                      <MonthlyDetails row={row} onEditIncome={onEditIncome} onEditExpense={onEditExpense} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <div className="hidden overflow-x-auto sm:block">
            <table className="w-full min-w-[860px] border-separate border-spacing-0 text-left text-sm">
              <thead>
                <tr className="text-xs font-semibold text-muted">
                  <th className="border-b border-border px-3 py-2">Month</th>
                  <th className="border-b border-border px-3 py-2 text-right">Income</th>
                  <th className="border-b border-border px-3 py-2 text-right">Expenses</th>
                  <th className="border-b border-border px-3 py-2 text-right">GST on income</th>
                  <th className="border-b border-border px-3 py-2 text-right">GST credits</th>
                  <th className="border-b border-border px-3 py-2 text-right">Net GST result</th>
                  <th className="border-b border-border px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const isExpanded = expanded === row.id;
                  return (
                    <React.Fragment key={row.id}>
                      <tr className="text-ink hover:bg-surface">
                        <td className="border-b border-border px-3 py-3 font-semibold">{row.label}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right">{currency(row.incomeTotal)}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right">{currency(row.expenseTotal)}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right text-payable">{currency(row.gstOnSales)}</td>
                        <td className="tabular border-b border-border px-3 py-3 text-right text-credit">{currency(row.gstCredits)}</td>
                        <td className="border-b border-border px-3 py-3 text-right">
                          <div className="flex flex-col items-end gap-1">
                            <span className={`tabular font-semibold ${row.netGst >= 0 ? "text-payable" : "text-credit"}`}>
                              {currency(Math.abs(row.netGst))}
                            </span>
                            <Badge variant={row.netGst >= 0 ? "payable" : "credit"}>
                              {row.netGst >= 0 ? "Payable" : "Refundable"}
                            </Badge>
                          </div>
                        </td>
                        <td className="border-b border-border px-3 py-3 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-expanded={isExpanded}
                            onClick={() => setExpanded(isExpanded ? null : row.id)}
                          >
                            Details
                            <ChevronDown className={`size-4 transition-transform ${isExpanded ? "rotate-180" : ""}`} aria-hidden="true" />
                          </Button>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr>
                          <td className="border-b border-border bg-surface px-3 py-4" colSpan={7}>
                            <MonthlyDetails row={row} onEditIncome={onEditIncome} onEditExpense={onEditExpense} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MonthlyDetails({
  row,
  onEditIncome,
  onEditExpense,
}: {
  row: MonthlyFinancialSummary;
  onEditIncome: (income: IncomeSummary) => void;
  onEditExpense: (expense: Expense) => void;
}) {
  return (
    <>
      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <SourceList
          title="Income sources"
          empty="No income for this month"
          items={row.incomeSummaries.map((income) => ({
            id: income.id,
            primary: `${income.periodStart} to ${income.periodEnd}`,
            secondary: `${income.sourceType === "document_scan" ? "Scanned source" : "Manual entry"} | GST on income ${currency(gstFromGross(income.grossIncome))} | deductions GST credit ${currency(income.platformFeesGst || 0)}`,
            amount: income.grossIncome,
            badge: income.provider,
            onEdit: () => onEditIncome(income),
          }))}
        />
        <SourceList
          title="Expense sources"
          empty="No expenses for this month"
          items={row.expenses.map((expense) => ({
            id: expense.id,
            primary: expense.supplier || expenseCategoryLabels[expense.category],
            secondary: `${expense.date} | ${expenseCategoryLabels[expense.category]} | Business use ${expense.businessUsePercent}% | GST credit ${currency(claimableGstForExpense(expense))}`,
            amount: expense.amount,
            badge: expense.entrySource === "document_scan" ? "Scanned source" : "Manual",
            onEdit: () => onEditExpense(expense),
          }))}
        />
      </div>
    </>
  );
}

function MobileMetric({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "credit" | "payable" }) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className={`tabular mt-1 font-semibold ${tone === "credit" ? "text-credit" : tone === "payable" ? "text-payable" : "text-ink"}`}>{value}</p>
    </div>
  );
}

function SourceList({
  title,
  empty,
  items,
}: {
  title: string;
  empty: string;
  items: Array<{ id: string; primary: string; secondary: string; amount: number; badge: string; onEdit: () => void }>;
}) {
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {items.length === 0 ? (
        <p className="mt-2 rounded-lg border border-dashed border-border bg-white p-3 text-sm text-muted">{empty}</p>
      ) : (
        <div className="mt-2 grid gap-2">
          {items.map((item) => (
            <div key={item.id} className="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-white p-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="min-w-0 max-w-full break-words font-semibold text-ink sm:truncate">{item.primary}</p>
                  <Badge variant="muted">{item.badge}</Badge>
                </div>
                <p className="mt-1 break-words text-xs leading-5 text-muted">{item.secondary}</p>
              </div>
              <div className="flex min-w-0 items-center justify-between gap-2 sm:shrink-0 sm:justify-end">
                <p className="tabular font-semibold text-ink">{currency(item.amount)}</p>
                <Button type="button" variant="ghost" size="icon" aria-label={`Edit source ${item.primary}`} title="Edit source" onClick={item.onEdit}>
                  <Pencil className="size-4" aria-hidden="true" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
